/**
 * SentReplySweepCard — "run the sweep now", offered by EVERY host.
 *
 * WHAT PROBLEM IT SOLVES. A run parks in `pending_email_send` until the
 * sent-reply sweep notices the AM actually sent the reply. In Azure a Container
 * App job runs that sweep on a schedule. On a local stack nothing drives it, so
 * a locally-launched run stays parked forever and the rest of the hand-off —
 * attach the sent mail to the activity, complete the workflow — is unreachable
 * while developing. This card is the missing trigger.
 *
 * IT PERFORMS NO TRANSPORT, for the same reason `CarrierEvidenceCard` performs
 * none: the presentation is shared by ora and the Outlook add-in (and whatever
 * host comes next), and those hosts do not reach a backend the same way. ora
 * submits through a Remix resource route; the add-in goes through
 * `orchestratorApi` to the Product API, which is how it reaches every substrate
 * `/admin/*` route it already uses (`checkEdocs` is the precedent). So this file
 * assumes NO router, NO fetch client and NO bundler — `onRunSweep` and the three
 * result props are the whole contract, and each host supplies them.
 *
 * THE LOCAL-ONLY GATE LIVES IN THE HOSTS, not here. ora gates on
 * `process.env.NODE_ENV`, the add-in on `import.meta.env.DEV` — two different
 * conventions belonging to two different build systems, neither of which can be
 * evaluated correctly from shared code. A host that may not sweep simply does
 * not render this card.
 *
 * The status check DOES live here: "only on a parked run" is a property of the
 * card, it is testable in one place, and it keeps the card honest if a future
 * host mounts it somewhere the drawer's status-family switch does not guard.
 *
 * WHAT IT REPORTS. The sweep takes no arguments — one pass checks EVERY mailbox
 * with outstanding work, and any long-lived stack carries a backlog of stale
 * pending rows. So the result is worded as a statement about the whole backlog,
 * never about "your" run, and the numbers are the substrate's own counts.
 * Per-outcome counts (matched / expired / not_found) exist only in the
 * substrate's run report, so the run id is surfaced as the handle on them
 * rather than inventing numbers the wire does not carry.
 */

/** The substrate's sweep response, as every host hands it back. */
export interface SentReplySweepOutcome {
  /** Monitor rows this pass actually fetched and checked. */
  pending: number;
  /** Distinct AM mailboxes those rows spanned. */
  mailboxes: number;
  /** Substrate run id — the grep handle for per-instance outcomes. */
  runId: string;
}

export interface SentReplySweepCardProps {
  /** The instance's effective status. Anything but `pending_email_send` renders nothing. */
  status: string | null | undefined;
  /** Starts one sweep pass. The host owns the transport and the local-dev gate. */
  onRunSweep: () => void | Promise<void>;
  /** True while the host's request is in flight. */
  pending?: boolean;
  /** The last successful sweep's counts, or null. */
  result?: SentReplySweepOutcome | null;
  /** The last failure, rendered inline. Never replaced by a stale result. */
  error?: string | null;
}

export function SentReplySweepCard({
  status,
  onRunSweep,
  pending = false,
  result,
  error,
}: SentReplySweepCardProps) {
  if (status !== "pending_email_send") return null;

  // The host owns the failure — it comes back as `error` — but a rejected
  // promise must not escape as an unhandled rejection in the task pane, which
  // has no error boundary above this card. Swallowing HERE never suppresses the
  // message: that is rendered from the prop.
  const fire = () => {
    void Promise.resolve(onRunSweep()).catch(() => undefined);
  };

  return (
    <div
      className="rounded-lg border border-primary-gray-light-200 bg-base-white p-3 mb-2 flex flex-col gap-1"
      data-testid="sent-reply-sweep-card"
    >
      <p className="text-2xs font-semibold uppercase tracking-wide text-primary-gray-light-500">
        Local development only
      </p>

      <p className="text-sm font-semibold text-primary-gray-light-800">
        Run the sent-reply sweep
      </p>

      <p className="text-xs text-primary-gray-light-700">
        Nothing schedules the sweep on a local stack, so a run parked here will
        not notice the reply was sent until you run one. In Azure a scheduled
        job does this every few minutes.
      </p>

      <div className="mt-1 flex items-center gap-2 flex-wrap">
        <button
          type="button"
          disabled={pending}
          onClick={fire}
          className="h-8 px-3 inline-flex items-center btn-primary rounded-lg text-2xs font-semibold disabled:opacity-60"
          data-testid="sent-reply-sweep-run"
        >
          {pending ? "Sweeping…" : "Run sweep now"}
        </button>

        {error && (
          <span
            role="alert"
            className="text-2xs text-primary-error-700"
            data-testid="sent-reply-sweep-error"
          >
            {error}
          </span>
        )}
      </div>

      {result && !error && (
        <p
          role="status"
          className="text-2xs text-primary-gray-light-700"
          data-testid="sent-reply-sweep-result"
        >
          {/* Deliberately "every parked run", not "this run": one pass checks
              the whole backlog, so a count above one is normal and says nothing
              about the instance on screen. */}
          Checked {result.pending} pending{" "}
          {result.pending === 1 ? "reply" : "replies"} across {result.mailboxes}{" "}
          {result.mailboxes === 1 ? "mailbox" : "mailboxes"} — every parked run,
          not just this one. If the reply was sent, this run moves on; a cold
          wait is reconciled about once a minute, so give it up to that long to
          catch up.
          {result.runId ? (
            <>
              {" "}
              Sweep run <span className="font-mono">{result.runId}</span> — grep
              the substrate log for per-instance outcomes.
            </>
          ) : null}
        </p>
      )}
    </div>
  );
}

export default SentReplySweepCard;
